// The Money HTTP wire contract (Slice 6; slice-plan.md:83-92; D27; ADR-0023).
//
// The SINGLE source of truth for the Money routes apps/api (producer) and apps/web (consumer)
// build against - pinned here so the two sides cannot drift (the Slice-2 wire-drift lesson).
// Success payloads are the domain schemas from ../model/money-view.js; every failure returns the
// shared `ErrorEnvelope { code, message }` (http/knowledge.ts:21) - a byte-identical not-found on
// an app that is not the caller's (app-security §2 A01 - existence is not revealed).
import { z } from "zod";
import { AppId } from "../ids.js";
import { BillingInboxItem, MoneyView } from "../model/money-view.js";
import { CeilingSource } from "../model/enums.js";
import { MinorUnits } from "../money.js";
/** The canonical route path strings, for the api lane to register verbatim (D2; `/v1/apps/:appId/...` idiom). */
export const MONEY_ROUTES = {
    moneyView: "/v1/apps/:appId/money-view",
    billingInbox: "/v1/apps/:appId/billing-inbox",
    monthlyLimit: "/v1/apps/:appId/monthly-limit",
    monthlyLimitKeep: "/v1/apps/:appId/monthly-limit/keep",
};
/** Path params for the app-scoped Money routes: the one app the request is scoped to. */
export const MoneyRouteParams = z.object({ appId: AppId });
// --- Money view: GET /v1/apps/:appId/money-view (the D27 read surface) ---
/** 200 -> the `MoneyView` (plan capacity, all-in cost, abstain-capable P&L), else an `ErrorEnvelope`. */
export const MoneyViewResponse = MoneyView;
// --- Billing inbox: POST /v1/apps/:appId/billing-inbox (a founder-supplied or imported item) ---
/**
 * POST body: one raw billing-inbox item. Omits the server-assigned `id`/`createdAt` and the
 * caller's own `accountId` (implicit from the session) - the ROUTE path `:appId` is authoritative
 * on any conflict with a body-carried app id (mirrors `BuildRequestBody`, http/build.ts:45-49).
 */
export const BillingInboxRequestBody = BillingInboxItem.omit({
    id: true,
    accountId: true,
    createdAt: true,
});
/** 200 -> the recorded `BillingInboxItem`, else an `ErrorEnvelope`. */
export const BillingInboxResponse = BillingInboxItem;
// --- Monthly limit: PATCH /v1/apps/:appId/monthly-limit (AC3 clause 2, one-tap adjust; D48) ---
/**
 * PATCH body: the founder's chosen monthly limit, in minor units of their PLAN'S currency (ADR-0039).
 *
 * NOT NULLABLE, deliberately. "Remove my limit" is a different decision from "change my limit" - it
 * turns off the guardrail that `strings.ceiling_line` promises ("we never go past it without
 * asking"), and it is not what AC3's "one-tap adjustable" asks for. A route that accepted null would
 * make clearing the limit a typo away from changing it. If removal is ever wanted it gets its own
 * verb and its own confirmation, not a null here.
 *
 * POSITIVE, for the same reason `plans_default_monthly_limit_positive` exists: a limit of zero pauses
 * every fix on the app, which reads to the founder as the product breaking rather than as a limit
 * being enforced. The upper bound is INT4's range, because the column is an integer and a value past
 * it is a database error rather than a validation message.
 */
export const MonthlyLimitRequestBody = z.object({
    monthlyLimitMinor: z.number().int().positive().max(2_147_483_647),
});
/** 200 -> the limit now stored and where it came from. The founder's own tap makes it `founder`. */
export const MonthlyLimitResponse = z.object({
    monthlyLimitMinor: MinorUnits,
    ceilingSource: CeilingSource,
});
//# sourceMappingURL=money.js.map